import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { IcPageHeader, IcButton, IcTextField, IcSelect, IcEmptyState } from "@ukic/react";

import { api, DECODER_FORMATS, type Decode, type Device } from "../api";
import { humanHz, timeAgo } from "../format";
import { DataTable, type Column } from "../components/DataTable";
import { Loading } from "../components/Loading";
import { ErrorState } from "../components/ErrorState";

const FORMAT_OPTIONS = [
  { label: "All formats", value: "" },
  ...DECODER_FORMATS.map((f) => ({ label: f.value, value: f.value })),
];

export default function Signals() {
  const [decodes, setDecodes] = useState<Decode[] | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [error, setError] = useState<unknown>(null);

  // filters (applied). A ?q=… URL param (e.g. from an Entities drill-down) seeds
  // the search so navigating to /signals?q=<tail|tmsi|imsi> shows that target.
  const [searchParams] = useSearchParams();
  const seedQ = searchParams.get("q") || "";
  const [deviceId, setDeviceId] = useState("");
  const [format, setFormat] = useState("");
  const [query, setQuery] = useState(seedQ); // committed search term
  const [queryInput, setQueryInput] = useState(seedQ); // text-field buffer
  const [tail, setTail] = useState(""); // aircraft registration filter
  const [flight, setFlight] = useState(""); // flight number filter

  const load = useCallback(() => {
    setError(null);
    api
      .listDecodes({
        device_id: deviceId || undefined,
        format: format || undefined,
        q: query || undefined,
        tail: tail || undefined,
        flight: flight || undefined,
        limit: 200,
      })
      .then(setDecodes)
      .catch(setError);
  }, [deviceId, format, query, tail, flight]);

  useEffect(() => {
    api.listDevices().then(setDevices).catch(() => setDevices([]));
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 15_000);
    return () => window.clearInterval(t);
  }, [load]);

  const deviceName = useMemo(() => {
    const m = new Map(devices.map((d) => [d.id, d.name]));
    return (id: string) => m.get(id) ?? id;
  }, [devices]);

  const deviceOptions = useMemo(
    () => [
      { label: "All devices", value: "" },
      ...devices.map((d) => ({ label: d.name, value: d.id })),
    ],
    [devices],
  );

  const columns: Column<Decode>[] = [
    { key: "ts", header: "Time", cell: (d) => timeAgo(d.ts) },
    { key: "device", header: "Device", cell: (d) => deviceName(d.device_id) },
    { key: "format", header: "Decoder", cell: (d) => <code>{d.format}</code> },
    { key: "protocol", header: "Protocol", cell: (d) => <code>{d.protocol || d.format}</code> },
    { key: "freq", header: "Freq", cell: (d) => (d.freq ? humanHz(d.freq) : "—") },
    { key: "tail", header: "Tail", cell: (d) => d.tail || <span className="hl-muted">—</span> },
    { key: "flight", header: "Flight", cell: (d) => d.flight || <span className="hl-muted">—</span> },
    { key: "mlabel", header: "Lbl", cell: (d) => d.mlabel || <span className="hl-muted">—</span> },
    { key: "label", header: "Summary", cell: (d) => d.label || <span className="hl-muted">—</span> },
    {
      key: "body",
      header: "Record",
      cell: (d) => (
        <code className="hl-decode-body" title={d.body}>
          {d.body.length > 120 ? d.body.slice(0, 120) + "…" : d.body}
        </code>
      ),
    },
  ];

  return (
    <div className="hl-page hl-stack">
      <IcPageHeader heading="Signals" subheading="Decoded radio data across the fleet">
        <IcButton slot="actions" variant="secondary" onClick={load}>
          Refresh
        </IcButton>
      </IcPageHeader>

      <div className="hl-panel hl-row">
        <IcTextField
          label="Search"
          placeholder="e.g. Acurite, temperature, an id…"
          value={queryInput}
          onIcInput={(e) => setQueryInput((e as CustomEvent<{ value: string }>).detail.value)}
          onKeyUp={(e) => {
            if (e.key === "Enter") setQuery(queryInput.trim());
          }}
        />
        <IcSelect
          label="Device"
          value={deviceId}
          options={deviceOptions}
          onIcChange={(e) => setDeviceId((e as CustomEvent<{ value: string }>).detail.value)}
        />
        <IcSelect
          label="Format"
          value={format}
          options={FORMAT_OPTIONS}
          onIcChange={(e) => setFormat((e as CustomEvent<{ value: string }>).detail.value)}
        />
        <IcTextField
          label="Tail"
          placeholder="e.g. D-AIGX"
          value={tail}
          onIcInput={(e) => setTail((e as CustomEvent<{ value: string }>).detail.value)}
        />
        <IcTextField
          label="Flight"
          placeholder="e.g. LH0560"
          value={flight}
          onIcInput={(e) => setFlight((e as CustomEvent<{ value: string }>).detail.value)}
        />
        <IcButton onClick={() => setQuery(queryInput.trim())}>Search</IcButton>
        {(query || tail || flight) && (
          <IcButton
            variant="tertiary"
            onClick={() => {
              setQueryInput("");
              setQuery("");
              setTail("");
              setFlight("");
            }}
          >
            Clear
          </IcButton>
        )}
      </div>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : decodes === null ? (
        <Loading label="Loading signals…" />
      ) : decodes.length === 0 ? (
        <IcEmptyState
          heading="No decoded signals yet"
          subheading="Start a decoder on a device (Device → Decoder) and matching records will appear here."
        />
      ) : (
        <DataTable
          caption="Decoded signals"
          columns={columns}
          rows={decodes}
          rowKey={(d) => String(d.id)}
        />
      )}
    </div>
  );
}
